import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import { fetchIndividualProduct, updateInternationalProduct } from "../../actions/International";
import { IMAGE_URL } from "../../path";

interface FormData {
    brand_id: number;
    name: string;
    description: string;
    image: File | null;
    hd_image: File | null;
    tasting_notes: File | null;
}

function EditInternationalProduct() {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const location = useLocation();
    const user = location.state?.user;
    const navigate = useNavigate();
    const { id } = useParams<{ id: string; productId: string }>();

    const [formData, setFormData] = useState<FormData>({
        brand_id: Number(id),
        name: "",
        description: "",
        image: null,
        hd_image: null,
        tasting_notes: null,
    });

    const [imagePreview, setImagePreview] = useState<{ [key: string]: string | null }>({
        image: null,
        hd_image: null,
    });

    const [pdfName, setPdfName] = useState<string | null>(null);

    // Original fetch call
    function fetchProduct() {
        fetchIndividualProduct(Number(id))
            .then(response => {
                const data = response.data.data;

                setFormData(prev => ({
                    ...prev,
                    name: data.name ?? "",
                    description: data.description ?? "",
                    // files stay null, user can upload new ones
                }));

                setImagePreview({
                    image: data.image ? IMAGE_URL + data.image : null,
                    hd_image: data.hd_image ? IMAGE_URL + data.hd_image : null,
                });

                setPdfName(data.tasting_note ? data.tasting_note.split("/").pop() : null);
            })
            .catch(error => console.error("Failed to fetch product:", error));
    }

    useEffect(() => {
        fetchProduct();
    }, [id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, type } = e.target;

        if (type === "file") {
            const file = (e.target as HTMLInputElement).files?.[0] || null;
            setFormData(prev => ({ ...prev, [name]: file }));

            if (file && name !== "tasting_notes") {
                setImagePreview(prev => ({ ...prev, [name]: URL.createObjectURL(file) }));
            } else if (name === "tasting_notes") {
                setPdfName(file?.name ?? null);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const payload = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                payload.append(key, value instanceof File ? value : value ?? "");
            });

            const response = await updateInternationalProduct(payload, Number(id));

            if (response.status === 200) {
                alert("Product successfully updated!");
                navigate(`/dashboard/international/brands/${response.data.data.brand_id}/products`, { state: { user } });
            }
        } catch (error) {
            console.error("Submit Error:", error);
            alert("Something went wrong submitting the form");
        }
    };

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />

            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <h2>Edit International Product</h2>

                <form onSubmit={handleSubmit} encType="multipart/form-data" style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    <input
                        type="text"
                        className="input-email"
                        placeholder="Product Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                    />

                    <textarea
                        className="input-email"
                        placeholder="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={6}
                        style={{ resize: "vertical" }}
                        required
                    />

                    <label>Product Image</label>
                    <input type="file" name="image" accept="image/*" onChange={handleInputChange} />
                    {imagePreview.image && <img src={imagePreview.image} style={{ width: "150px", marginTop: "10px" }} />}

                    <label>HD Image</label>
                    <input type="file" name="hd_image" accept="image/*" onChange={handleInputChange} />
                    {imagePreview.hd_image && <img src={imagePreview.hd_image} style={{ width: "150px", marginTop: "10px" }} />}

                    <label>Tasting Note (PDF)</label>
                    <input type="file" name="tasting_notes" accept="application/pdf" onChange={handleInputChange} />
                    {pdfName && <p>📄 {pdfName}</p>}

                    <button type="submit" className="btn btn-success">Update Product</button>
                </form>
            </div>
        </div>
    );
}

export default EditInternationalProduct;
